import type { CollisionRules, CompileEntityType, CompileObjectType, SurfaceRules } from "../compile/context.ts";
import type { DensityDefinition } from "./density.ts";
import { validateDensity } from "./density-registration.ts";
import { validateFootprintRules, type FootprintRule, type PropFrameInfo } from "./footprint-policy.ts";
import type { TerrainDefinition } from "../terrain/types.ts";
import type { MapDocument, TileRef, WorldDocument } from "../document/types.ts";
import { assertZoneStampTypes, soundSetChoice, validateObjectType, type ObjectTypeDefinition } from "./objects.ts";
import type { ValidationScope } from "./validation-scope.ts";
import { validateBorderBand, type BorderBandKind } from "./border-band.ts";
import { validateZoneTypes, type ZoneTypeDefinition } from "./zone-types.ts";
import { validateArrivalType, validateAtmosphere, validateRoomZone, type AtmosphereLists } from "./room-registration.ts";
import { validateEntityType, validateReferenceTargets, validateSchema, type EntityTypeDefinition, type JsonSchema } from "./schema.ts";

export type { BorderBandKind } from "./border-band.ts";
export { MAX_ZONE_DESCRIPTION, validateZoneTypes, type ZoneTypeDefinition } from "./zone-types.ts";
export { compileArrivalType, compileAtmosphere, compileDoorFields } from "./adapter-compile.ts";

/**
 * How a game plugs into the editor (Playbook 5.7, ADR-024, ADR-028): the entity types it places, the
 * collision rules and brushes it declares, the footprint policy as rules (ADR-052), the fields a map carries
 * (`mapSchema`, ADR-052's amendment of 2026-09-26, an optional registration), an optional check
 * over a map, the frame it suggests for a behaviour's further state (ADR-053), and hooks after a save and
 * before a playtest. The editor never imports a game; the game hands its adapter to `EditorStore`.
 */

const MAX_BYTE = 255;

/**
 * A value an author can paint into a collision layer, named for the palette.
 */
export interface CollisionBrush {
  readonly name: string;
  readonly value: number;
}

/**
 * One problem the editor found in a map, from a game's `validate` hook or from Daedalus's own checks.
 * `layerId` with `entityId`, `propId` or `doorId` locates it when it concerns one placed thing or one door.
 * `severity` is `warning` for something the author should know that does not stop the compile, such as a
 * behaviour whose frame blocks nothing (P1.1-T6), and `info` for something that may well be meant, such as a
 * one-way door (ADR-122 decision 2.4), which stops nothing either; absent, the compile refuses the map until it
 * is fixed. `message` says what
 * is wrong, then `; ` and the fix; the editor's Issues list shows the two apart and names what the message
 * names by id (`issueEntries`), and a compile's refusal quotes the message whole. `remedy` names a fix the
 * editor can make itself, which the Issues list offers as a button on the line (`IssueRemedy`). `kind` is the
 * id the check declares for what it reports, lower-case words joined by `.` or `-` such as `prop.off-cells`
 * (ADR-070's amendment of 2026-10-05): an ignore binds to it and to the thing the issue points at, never to
 * the message, which may be reworded. `ignorable` true lets the author ignore the issue; it counts only on a
 * warning or an info that declares a kind, so nothing that stops the compile can be ignored, and an issue that leaves it
 * out cannot be. `frame` is the frame an issue about no placed thing is about, which its fix acts on. `box`, in world pixels, is the
 * stretch of the map an issue about no placed thing is about, which Go to frames (the seam checks' runs).
 */
export interface ValidationIssue {
  readonly mapId: string;
  readonly layerId?: string;
  readonly entityId?: string;
  readonly propId?: string;
  readonly doorId?: string;
  readonly message: string;
  readonly severity?: "warning" | "info";
  readonly remedy?: IssueRemedy;
  readonly alternative?: IssueRemedy;
  readonly kind?: string;
  readonly ignorable?: true;
  readonly frame?: TileRef;
  readonly box?: { readonly x: number; readonly y: number; readonly w: number; readonly h: number };
  /**
   * The offer of a seam continuity issue about a span that is not fixed (ADR-109, P1.6-T152): the crossing of the
   * seam `a/b` named `name`, with the corner numbers of map `a`'s edge the painted edge proposes. It is the
   * payload of a `setCrossing`; the issue changes nothing.
   */
  readonly moveCrossing?: { readonly a: string; readonly b: string; readonly name: string; readonly from: number; readonly to: number };
}

/**
 * What a terrain is for the seam continuity check (`DaedalusGameAdapter.seamGround`).
 */
export type SeamGround = "path" | "water" | "blocked";

/**
 * A fix the editor makes for an issue in one step (`EditorStore.fixIssue`, ADR-040 amendment of 2026-10-03,
 * P1.6-T82): `align-prop` moves the issue's prop onto the collision cells together with the pieces it is
 * composed with, `align-prop-alone` moves that prop alone, against them, and `keep-prop-placement` moves nothing
 * and records on the prop that it stands where it does on purpose (ADR-040 amendment of 2026-10-04, P1.6-T88).
 * `remove-pull` takes the pulled frame the issue names as `frame` out of the world's tileset list (ADR-129
 * decision 3.12). `remove-layer` takes the layer the issue is about out of the map. An issue names its fix as `remedy` and, where the author may instead accept what the issue
 * reports, that as `alternative`.
 */
export type IssueRemedy = "align-prop" | "align-prop-alone" | "keep-prop-placement" | "remove-pull" | "remove-layer";

/**
 * One frame of a spritesheet a game's stand-in draws with (ADR-071 decision 1): the sheet's URL, its frame
 * size, the frame's index, and the pivot, its feet, which stands on the feet point. World pixels.
 */
export interface StandInArt {
  readonly url: string;
  readonly frameWidth: number;
  readonly frameHeight: number;
  readonly frame: number;
  readonly pivot: { readonly x: number; readonly y: number };
}

/**
 * How the occluder fade decides (ADR-040), all of it the game's to set: how far a prop's art must rise above
 * its base, in whole pixels, before it may fade at all; the least share of the body box its solid pixels must
 * hide, above 0 and at most 1; and the alpha, from 0 to 1, a prop that hides it draws at.
 */
export interface OccluderRule {
  readonly minHeight: number;
  readonly minCoverage: number;
  readonly alpha: number;
}

/**
 * How large a game's character is, for the editor's player layer (ADR-071 decision 1), in world pixels:
 * the body box, whose bottom centre is the feet point; the feet box the game's inside test reads,
 * `halfWidth` either side of the feet point and `height` above the feet line, which is the box its movement
 * stops a character by, with `step`, the distance between two positions a character can stand at (the
 * footprint panel moves the box by it, ADR-113 decision 14; a pixel when left out); when the game has it, the art
 * the stand-in draws; and, when the game fades props over its character, the rule it fades by, so the
 * stand-in fades what the game's local character fades (decision 10). Code, never served: the contract does
 * not carry it.
 */
export interface StandInDefinition {
  readonly width: number;
  readonly height: number;
  readonly feetBox: { readonly halfWidth: number; readonly height: number; readonly step?: number };
  readonly art?: StandInArt;
  readonly occluder?: OccluderRule;
}

/**
 * How much of the world a game's camera shows (P1.6-T47), in world pixels: `view`, the width and height its
 * camera sees at the game's default zoom. The editor's camera-stop guide insets each map edge by half of it,
 * where a camera bounded by the map stops following the player. Code, never served: the contract does not
 * carry it.
 */
export interface CameraDefinition {
  readonly view: { readonly width: number; readonly height: number };
}

/**
 * The zone type unpainted ground plays as, when the game marks one.
 *
 * @param adapter The game's adapter.
 * @returns The type, or null when the game marks none or registers no zone types.
 */
export function unpaintedZoneType(adapter: DaedalusGameAdapter): ZoneTypeDefinition | null {
  return adapter.zoneTypes?.find((type) => type.unpainted === true) ?? null;
}

/**
 * The zone type ids a compile context carries (ADR-054's amendment of 2026-09-27, decision 6).
 *
 * @param adapter The game's adapter.
 * @returns The ids in registration order, empty when the game registers none.
 */
export function compileZoneTypes(adapter: DaedalusGameAdapter): string[] {
  return (adapter.zoneTypes ?? []).map((type) => type.id);
}

/**
 * What a game is asked the surface of: a terrain, by its id and its name, or a tileset, whose name is its id.
 */
export interface GroundSource {
  readonly kind: "terrain" | "tileset";
  readonly id: string;
  readonly name: string;
}

/**
 * A game's registration with the editor. `startMap` names the map the game starts players on (ADR-021's
 * amendment "several maps"): the editor never removes it and a host may open it first. Code, never served.
 * `zoneTypes` lists the zone types its authors paint, in order (ADR-054's amendment of 2026-09-27); a game
 * that registers none gets no Zones mode and no zone layer kind. `density` registers the groups and budgets the
 * density meter counts and grades (ADR-088 decision 4); a game that registers none gets no meter. `propGround`
 * and `propNoFade` name, by a prop frame's catalogue entry, the frames the asset build marks `ground` and `noFade`
 * (ADR-040 and its amendment of 2026-10-03), each the default a frame's own override in the library wins over (`FootprintOverride`); a game that registers neither marks none by default. `groundSurface` names what a
 * terrain or a tileset is made of, in the game's own words, or nothing for what is not ground; the compile writes the
 * names into the client chunks (`compile/surfaces.ts`) and reads nothing into them, and a game that registers none
 * gets no surfaces. `surfaceNames` lists every surface the game has, in its own words, for a footprint's deck to
 * be given one (ADR-017's amendment of 2026-10-05); a game that lists none offers no choice.
 */
export interface DaedalusGameAdapter {
  readonly entityTypes: readonly EntityTypeDefinition[];
  readonly objectTypes?: readonly ObjectTypeDefinition[];
  readonly collision?: (terrains: readonly TerrainDefinition[]) => CollisionRules;
  readonly collisionBrushes?: readonly CollisionBrush[];
  readonly footprintPolicy?: readonly FootprintRule[];
  readonly propGround?: (frame: PropFrameInfo) => boolean;
  readonly propNoFade?: (frame: PropFrameInfo) => boolean;
  readonly groundSurface?: (ground: GroundSource) => string | null;
  readonly surfaceNames?: readonly string[];
  readonly suggestFrame?: (type: string, stateId: string, placedName: string) => string | undefined;
  /**
   * The strip an animation of a type should play, by name, asked as a behaviour is switched on for a prop (ADR-096
   * decision 40): once for each animation the type's transitions name.
   *
   * @param type The object type id.
   * @param animation The animation's name, a transition's `plays`.
   * @param placedName The name of the frame the prop carries, as the catalogue lists it.
   * @param names The names of every prop frame of the placed frame's tileset, in the catalogue's order.
   * @returns The strip's first frame's name, or undefined to leave the animation for the author to choose.
   */
  readonly suggestAnimation?: (type: string, animation: string, placedName: string, names: readonly string[]) => string | undefined;
  /**
   * The game's own checks of one map.
   *
   * @param map The map, its followed zones lowered to cells.
   * @param scope The world's tile size and compile context, which a check of where a prop's frame puts something
   *   reads; the editor and `validateMap` always hand it over.
   * @returns The issues.
   */
  readonly validate?: (map: MapDocument, scope?: ValidationScope) => readonly ValidationIssue[];
  /**
   * Runs after a save writes the world; a save with nothing to write (the document is the text last saved or
   * pulled) does not run it.
   *
   * @param world The document the save wrote.
   * @returns Settles when the game's work on the saved world is done.
   */
  readonly onSave?: (world: WorldDocument) => Promise<void>;
  readonly onPlaytest?: (mapId: string) => Promise<void>;
  readonly standIn?: StandInDefinition;
  readonly camera?: CameraDefinition;
  readonly mapSchema?: JsonSchema;
  readonly startMap?: string;
  readonly zoneTypes?: readonly ZoneTypeDefinition[];
  /**
   * The kinds of thing kept out of the border band, one chunk from an edge with a placed neighbour (ADR-109
   * decision 6): the seam checks warn for each placed thing of a kind inside it. A game that lists none gets none.
   */
  readonly borderBand?: readonly BorderBandKind[];
  /**
   * What a terrain is for the seam continuity check (ADR-109 decision 6, P1.6-T152): `path` and `water` are what a
   * crossing is painted with, `blocked` is ground nothing crosses (a cliff, a dyke), and nothing is open ground.
   * Daedalus names no terrain; a game that leaves it out gets none of the class checks.
   */
  readonly seamGround?: (terrain: { readonly id: string; readonly name: string }) => SeamGround | undefined;
  readonly density?: DensityDefinition;
  /** The zone type a new room's floor is painted with (ADR-122 decision 3.1); one of `zoneTypes`. */
  readonly roomZone?: string;
  /** The music and ambience names a map's atmosphere may take (ADR-122 decision 6). */
  readonly atmosphere?: AtmosphereLists;
  /** The entity type Daedalus treats as an arrival point (ADR-122 decision 2.2); one of `entityTypes`. */
  readonly arrivalType?: string;
  /**
   * The game's settings for a door (ADR-122 decision 2.1), as an entity type's `schema`: Daedalus edits,
   * validates and compiles each door's `fields` against it without reading their meaning, and a field a door lacks
   * compiles as its default.
   */
  readonly doorFields?: JsonSchema;
  /** The game's word for a map group, capitalised, shown on the Maps tab and in the editor's sentences (P1.6-T276); a game that leaves it out gets "Map group". Code, never served: the contract does not carry it, so an adapter built from a contract has none. */
  readonly mapGroupLabel?: string;
}

/**
 * The word the editor uses for a map group when a game gives none.
 */
export const DEFAULT_MAP_GROUP_LABEL = "Map group";

/**
 * A game's word for a map group.
 *
 * @param adapter The game's adapter.
 * @returns Its `mapGroupLabel`, or "Map group" when it has none.
 */
export function mapGroupLabelOf(adapter: DaedalusGameAdapter): string {
  return adapter.mapGroupLabel ?? DEFAULT_MAP_GROUP_LABEL;
}

/**
 * Checks an adapter before the editor uses it: every entity type's schema is within the supported subset,
 * no type name is registered twice, every collision brush has a unique name and a byte value, every
 * footprint rule matches on non-empty text with a policy the derivation understands, every reference field
 * names a registered type it can reach (`validateReferenceTargets`), a map schema, when registered, keeps to the subset an entity type's schema does (`validateSchema`), and a stand-in, when
 * registered, has whole positive sizes, art whose pivot lies on its frame and an occluder rule within its
 * ranges (`validateStandIn`), and a camera, when registered, has a view of whole positive pixels, a start map, when named, is not empty, the zone types, when registered, pass `validateZoneTypes`, a room's zone type, when named, is one of them (`validateRoomZone`), the atmosphere lists, when registered, pass `validateAtmosphere`, an arrival type, when named, is a registered entity type (`validateArrivalType`), the door fields, when registered, keep to the subset an entity type's schema does (`validateSchema`), and a density, when registered, passes `validateDensity`.
 *
 * @param adapter The game's adapter.
 * @returns The same adapter.
 * @throws Error naming the offending type, brush, rule, map schema property, stand-in field, camera field,
 *   zone type or density rule (`validateDensity`).
 */
export function registerAdapter<T extends DaedalusGameAdapter>(adapter: T): T {
  const seen = new Set<string>();
  for (const definition of adapter.entityTypes) {
    validateEntityType(definition);
    if (seen.has(definition.type)) {
      throw new Error(`entity type ${definition.type} is registered twice`);
    }
    seen.add(definition.type);
  }
  for (const definition of adapter.objectTypes ?? []) {
    validateObjectType(definition);
    if (seen.has(definition.type)) {
      throw new Error(`object type ${definition.type} is registered twice, as an entity or object type`);
    }
    seen.add(definition.type);
  }
  validateReferenceTargets(adapter.entityTypes, adapter.objectTypes ?? []);
  const brushes = new Set<string>();
  for (const brush of adapter.collisionBrushes ?? []) {
    if (!Number.isInteger(brush.value) || brush.value < 0 || brush.value > MAX_BYTE) {
      throw new Error(`collision brush ${brush.name} has value ${brush.value}, not a byte`);
    }
    if (brushes.has(brush.name)) {
      throw new Error(`collision brush ${brush.name} is registered twice`);
    }
    brushes.add(brush.name);
  }
  validateFootprintRules(adapter.footprintPolicy ?? [], "footprintPolicy");
  if (adapter.mapSchema !== undefined) {
    validateSchema(adapter.mapSchema, "map schema");
  }
  if (adapter.standIn !== undefined) {
    validateStandIn(adapter.standIn);
  }
  if (adapter.camera !== undefined) {
    if (adapter.camera.view === undefined || adapter.camera.view === null) {
      throw new Error("camera.view is missing; name the width and height the camera sees at the default zoom");
    }
    wholeAtLeast(adapter.camera.view.width, 1, "camera.view.width");
    wholeAtLeast(adapter.camera.view.height, 1, "camera.view.height");
  }
  if (adapter.startMap === "") {
    throw new Error("startMap is empty; name the map the game starts players on, or leave it out");
  }
  if (adapter.mapGroupLabel !== undefined && adapter.mapGroupLabel.trim() === "") {
    throw new Error("mapGroupLabel is empty; name the game's word for a map group, or leave it out");
  }
  validateZoneTypes(adapter.zoneTypes ?? []);
  validateBorderBand(adapter.borderBand ?? []);
  validateRoomZone(adapter.roomZone, compileZoneTypes(adapter));
  validateArrivalType(adapter.arrivalType, adapter.entityTypes.map((type) => type.type));
  if (adapter.atmosphere !== undefined) {
    validateAtmosphere(adapter.atmosphere);
  }
  if (adapter.doorFields !== undefined) {
    validateSchema(adapter.doorFields, "door fields");
  }
  assertZoneStampTypes(adapter.objectTypes ?? [], (adapter.zoneTypes ?? []).map((type) => type.id));
  if (adapter.density !== undefined) {
    validateDensity(adapter.density, adapter.entityTypes, adapter.camera !== undefined);
  }
  return adapter;
}

/**
 * Refuses a value that is not an integer of at least a least value.
 *
 * @param value The value.
 * @param least The least value allowed.
 * @param name The field's path, for the message.
 * @throws Error naming the field.
 */
function wholeAtLeast(value: number, least: number, name: string): void {
  if (!Number.isInteger(value) || value < least) {
    throw new Error(`${name} is ${value}, not ${least === 0 ? "a whole number of 0 or more" : "a positive whole number"}`);
  }
}

/**
 * Checks a stand-in's occluder rule (ADR-071 decision 10): a whole least height of 0 or more, a least share
 * above 0 and at most 1, and an alpha from 0 to 1.
 *
 * @param rule The rule.
 * @throws Error naming the offending field.
 */
function validateOccluder(rule: OccluderRule): void {
  wholeAtLeast(rule.minHeight, 0, "standIn.occluder.minHeight");
  if (!Number.isFinite(rule.minCoverage) || rule.minCoverage <= 0 || rule.minCoverage > 1) {
    throw new Error(`standIn.occluder.minCoverage is ${rule.minCoverage}, not a share above 0 and at most 1`);
  }
  if (!Number.isFinite(rule.alpha) || rule.alpha < 0 || rule.alpha > 1) {
    throw new Error(`standIn.occluder.alpha is ${rule.alpha}, not an alpha from 0 to 1`);
  }
}

/**
 * Checks a stand-in (ADR-071 decision 1): the body box and the feet box's height are positive integers,
 * the feet box's half width an integer of 0 or more and its step, when given, a positive integer; art has a URL, a positive whole frame size, a whole
 * frame index of 0 or more, and a whole pivot on its frame, both edges included, as a character sheet's
 * feet line may sit one row below its frame's last opaque row; and an occluder rule, when registered, by
 * `validateOccluder`.
 *
 * @param standIn The stand-in.
 * @throws Error naming the offending field.
 */
function validateStandIn(standIn: StandInDefinition): void {
  wholeAtLeast(standIn.width, 1, "standIn.width");
  wholeAtLeast(standIn.height, 1, "standIn.height");
  wholeAtLeast(standIn.feetBox.halfWidth, 0, "standIn.feetBox.halfWidth");
  wholeAtLeast(standIn.feetBox.height, 1, "standIn.feetBox.height");
  if (standIn.feetBox.step !== undefined) {
    wholeAtLeast(standIn.feetBox.step, 1, "standIn.feetBox.step");
  }
  if (standIn.occluder !== undefined) {
    validateOccluder(standIn.occluder);
  }
  const art = standIn.art;
  if (art === undefined) {
    return;
  }
  if (typeof art.url !== "string" || art.url === "") {
    throw new Error("standIn.art.url is empty");
  }
  wholeAtLeast(art.frameWidth, 1, "standIn.art.frameWidth");
  wholeAtLeast(art.frameHeight, 1, "standIn.art.frameHeight");
  wholeAtLeast(art.frame, 0, "standIn.art.frame");
  const { x, y } = art.pivot;
  if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x > art.frameWidth || y > art.frameHeight) {
    throw new Error(`standIn.art.pivot (${x}, ${y}) does not lie on its ${art.frameWidth} by ${art.frameHeight} frame`);
  }
}

/**
 * The game's surface names as a compile context carries them: one for each terrain, asked by its name, and each
 * tileset, asked by its id, that the game names a surface for.
 *
 * @param adapter The game's adapter.
 * @param terrains Terrains of the context.
 * @param tilesets The ids of the tilesets a world may place tiles of.
 * @returns The names by terrain id and by tileset id, with the names the game lists when it lists any, or undefined
 *   when the game names no surfaces.
 * @throws Error when the game names a blank surface, or lists a blank or repeated one.
 */
export function compileSurfaces(adapter: DaedalusGameAdapter, terrains: readonly TerrainDefinition[], tilesets: readonly string[]): SurfaceRules | undefined {
  const name = adapter.groundSurface;
  if (name === undefined) {
    return undefined;
  }
  /**
   * The game's surface for one terrain or tileset.
   *
   * @param ground What is asked about.
   * @returns The surface's name, or null when the game names none.
   * @throws Error when the name is blank.
   */
  const named = (ground: GroundSource): string | null => {
    const surface = name(ground);
    if (surface !== null && surface.trim() === "") {
      throw new Error(`groundSurface: the surface of ${ground.kind} ${ground.id} is blank`);
    }
    return surface;
  };
  const byTerrain: Record<string, string> = {};
  for (const terrain of terrains) {
    const surface = named({ kind: "terrain", id: terrain.id, name: terrain.name });
    if (surface !== null) {
      byTerrain[terrain.id] = surface;
    }
  }
  const byTileset: Record<string, string> = {};
  for (const id of tilesets) {
    const surface = named({ kind: "tileset", id, name: id });
    if (surface !== null) {
      byTileset[id] = surface;
    }
  }
  const listed = adapter.surfaceNames;
  if (listed !== undefined && (listed.some((surface) => surface.trim() === "") || new Set(listed).size !== listed.length)) {
    throw new Error("surfaceNames: a listed surface is blank or repeated");
  }
  return { terrains: byTerrain, tilesets: byTileset, ...(listed === undefined ? {} : { names: [...listed] }) };
}

/**
 * The adapter's collision rules for a set of terrains, as a compile context carries them: the game's rules,
 * marked `passages` when it declares an open mask, since this build lays a placed prop's passage under it
 * (ADR-017's amendment of 2026-10-05). The mark is the build's, not the game's, so a context written by an
 * earlier build is told from one written by this.
 *
 * @param adapter The game's adapter.
 * @param terrains Terrains of the context.
 * @returns The rules, or none with `outside` 0 when the game declares no collision.
 */
export function compileCollision(adapter: DaedalusGameAdapter, terrains: readonly TerrainDefinition[]): CollisionRules {
  const rules = adapter.collision?.(terrains) ?? { rules: [], outside: 0 };
  return (rules.open ?? 0) === 0 ? rules : { ...rules, passages: true };
}

/**
 * The adapter's entity types as a compile context carries them.
 *
 * @param adapter The game's adapter.
 * @returns Type names and server-only flags, in registration order.
 */
export function compileEntityTypes(adapter: DaedalusGameAdapter): CompileEntityType[] {
  return registeredEntityTypes(adapter).map((t) => ({ type: t.type, serverOnly: t.serverOnly }));
}

/**
 * Every type the editor places as an entity. Object types are not among them (ADR-053): a behaviour is
 * switched on a placed prop, not placed as an entity of its own.
 *
 * @param adapter The adapter.
 * @returns The definitions, in registration order.
 */
export function registeredEntityTypes(adapter: DaedalusGameAdapter): EntityTypeDefinition[] {
  return [...adapter.entityTypes];
}

/**
 * The entity types the editor offers a person for placing: every registered type the game has not retired. A
 * retired type is still registered, so the store places one when asked by type and every reader of a saved entity
 * finds its definition.
 *
 * @param adapter The adapter.
 * @returns The definitions, in registration order.
 */
export function offeredEntityTypes(adapter: DaedalusGameAdapter): EntityTypeDefinition[] {
  return adapter.entityTypes.filter((type) => type.retired !== true);
}

/**
 * Reduces the adapter's object types to what compilation needs: the states with their footprint policies,
 * the initial state, the inside state of a type driven by presence (ADR-058 amendment, P1.3-T4), and the
 * transitions that play an animation by their states, animation name and direction (ADR-058 and its
 * amendment of 2026-10-03), left out on a type none of whose transitions plays one.
 *
 * @param adapter The adapter.
 * @returns The compile object types, in registration order.
 */
export function compileObjectTypes(adapter: DaedalusGameAdapter): CompileObjectType[] {
  return (adapter.objectTypes ?? []).map((t) => {
    const transitions = t.transitions.flatMap((transition) => (transition.plays === undefined ? [] : [{ from: transition.from, to: transition.to, plays: transition.plays, ...(transition.reverse === true ? { reverse: true as const } : {}) }]));
    const soundSet = soundSetChoice(t);
    return { type: t.type, states: t.states.map((s) => ({ id: s.id, footprint: s.footprint })), initial: t.initial, ...(t.presence === undefined ? {} : { inside: t.presence.inside }), ...(transitions.length === 0 ? {} : { transitions }), ...(soundSet === undefined ? {} : { soundSet }), ...(t.hiddenOnMap === true ? { hiddenOnMap: true as const } : {}) };
  });
}
